import { useState, useMemo } from "react";
import {
  Building2,
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Activity,
  UserCheck,
  Calendar,
  PieChart,
  BarChart3,
  Target,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { Progress } from "../../../../components/ui/progress";
import { cn } from "../../../../lib/utils";

export default function DepartmentAnalytics({ departments = [] }) {
  const [selectedMetric, setSelectedMetric] = useState("overview");

  // Calculate analytics
  const analytics = useMemo(() => {
    const totalDepartments = departments.length;
    const activeDepartments = departments.filter((d) => d.actif).length;
    const totalEmployees = departments.reduce(
      (sum, d) => sum + (d._count?.employes || 0),
      0
    );
    const totalBudget = departments.reduce(
      (sum, d) => sum + (parseFloat(d.budget) || 0),
      0
    );
    
    const averageBudget = totalBudget / totalDepartments || 0;

    // Budget utilization (estimated)
    const budgetUtilization = departments.map((d) => {
      const budget = parseFloat(d.budget) || 0;
      return {
        name: d.nom,
        budget: budget,
        employees: d._count?.employes || 0,
        utilization:
          budget && d._count?.employes
            ? Math.min(((d._count.employes * 50000) / budget) * 100, 100)
            : 0,
      };
    });

    // Department sizes
    const departmentSizes = departments
      .map((d) => ({
        name: d.nom,
        employees: d._count?.employes || 0,
        budget: parseFloat(d.budget) || 0,
        active: d.actif,
        manager: d.manager
          ? `${d.manager.prenom} ${d.manager.nom}`
          : "Non assigné",
      }))
      .sort((a, b) => b.employees - a.employees);

    // Budget distribution
    const budgetDistribution = departments
      .filter((d) => parseFloat(d.budget) > 0)
      .map((d) => {
        const budget = parseFloat(d.budget) || 0;
        return {
          name: d.nom,
          budget: budget,
          percentage: (budget / totalBudget) * 100,
        };
      })
      .sort((a, b) => b.budget - a.budget);

    // Performance metrics
    const performanceMetrics = {
      efficiency: (activeDepartments / totalDepartments) * 100,
      avgEmployeesPerDept: totalEmployees / totalDepartments || 0,
      budgetPerEmployee: totalEmployees > 0 ? totalBudget / totalEmployees : 0,
    };

    return {
      totalDepartments,
      activeDepartments,
      totalEmployees,
      totalBudget,
      averageBudget,
      budgetUtilization,
      departmentSizes,
      budgetDistribution,
      performanceMetrics,
    };
  }, [departments]);

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return new Intl.NumberFormat("fr-MA", {
        style: "currency",
        currency: "MAD",
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(amount);
    }
    return new Intl.NumberFormat("fr-MA", {
      style: "currency",
      currency: "MAD",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (number) => {
    if (number >= 1000000) {
      return `${(number / 1000000).toFixed(1)}M`;
    }
    if (number >= 1000) {
      return `${(number / 1000).toFixed(1)}K`;
    }
    return number.toString();
  };

  const getColorByIndex = (index) => {
    const colors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-purple-500",
      "bg-orange-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-yellow-500",
      "bg-red-500",
    ];
    return colors[index % colors.length];
  };

  if (departments.length === 0) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500 text-lg">Aucune donnée disponible</p>
        <p className="text-gray-400 text-sm">
          Ajoutez des départements pour voir les analyses
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-4">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="border-l-4 border-l-blue-500 min-h-[120px]">
          <CardContent className="p-4 h-full">
            <div className="flex items-start justify-between space-x-3 h-full">
              <div className="flex-1 min-w-0 flex flex-col justify-center">
                <p className="text-sm text-gray-600 mb-1">Total Départements</p>
                <p className="text-2xl font-bold text-gray-900 truncate">
                  {analytics.totalDepartments}
                </p>
                <p className="text-xs text-green-600 flex items-center mt-1">
                  <TrendingUp className="h-3 w-3 mr-1 flex-shrink-0" />
                  <span className="truncate">
                    {analytics.activeDepartments} actifs
                  </span>
                </p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                <Building2 className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500 min-h-[120px]">
          <CardContent className="p-4 h-full">
            <div className="flex items-start justify-between space-x-3 h-full">
              <div className="flex-1 min-w-0 flex flex-col justify-center">
                <p className="text-sm text-gray-600 mb-1">Total Employés</p>
                <p className="text-2xl font-bold text-gray-900 truncate">
                  {formatNumber(analytics.totalEmployees)}
                </p>
                <p className="text-xs text-gray-500 mt-1 truncate">
                  Moy.{" "}
                  {analytics.performanceMetrics.avgEmployeesPerDept.toFixed(1)}
                  /dept
                </p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                <Users className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500 min-h-[120px]">
          <CardContent className="p-4 h-full">
            <div className="flex items-start justify-between space-x-3 h-full">
              <div className="flex-1 min-w-0 flex flex-col justify-center">
                <p className="text-sm text-gray-600 mb-1">Budget Total</p>
                <p
                  className="text-2xl font-bold text-gray-900 truncate"
                  title={formatCurrency(analytics.totalBudget)}
                >
                  {formatCurrency(analytics.totalBudget)}
                </p>
                <p
                  className="text-xs text-gray-500 mt-1 truncate"
                  title={formatCurrency(analytics.averageBudget)}
                >
                  Moy. {formatCurrency(analytics.averageBudget)}
                </p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500 min-h-[120px]">
          <CardContent className="p-4 h-full">
            <div className="flex items-start justify-between space-x-3 h-full">
              <div className="flex-1 min-w-0 flex flex-col justify-center">
                <p className="text-sm text-gray-600 mb-1">Efficacité</p>
                <p className="text-2xl font-bold text-gray-900 truncate">
                  {analytics.performanceMetrics.efficiency.toFixed(0)}%
                </p>
                <p
                  className="text-xs text-gray-500 mt-1 truncate"
                  title={formatCurrency(
                    analytics.performanceMetrics.budgetPerEmployee
                  )}
                >
                  {formatCurrency(
                    analytics.performanceMetrics.budgetPerEmployee
                  )}
                  /emp
                </p>
              </div>
              <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
                <Target className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Department Sizes Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              Taille des Départements
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {analytics.departmentSizes.slice(0, 8).map((dept, index) => (
              <div key={dept.name} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <div
                      className={cn(
                        "w-3 h-3 rounded-full flex-shrink-0",
                        getColorByIndex(index)
                      )}
                    />
                    <span className="font-medium truncate">{dept.name}</span>
                    <Badge
                      variant={dept.active ? "default" : "secondary"}
                      className="text-xs flex-shrink-0"
                    >
                      {dept.active ? "Actif" : "Inactif"}
                    </Badge>
                  </div>
                  <span className="font-semibold text-gray-900 flex-shrink-0 ml-2">
                    {dept.employees}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Progress
                    value={
                      (dept.employees /
                        Math.max(
                          ...analytics.departmentSizes.map((d) => d.employees)
                        )) *
                      100
                    }
                    className="flex-1 h-2"
                  />
                  <span className="text-xs text-gray-500 truncate max-w-24">
                    {dept.manager}
                  </span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Budget Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <PieChart className="h-5 w-5 mr-2" />
              Répartition du Budget
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {analytics.budgetDistribution.slice(0, 8).map((dept, index) => (
              <div key={dept.name} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <div
                      className={cn(
                        "w-3 h-3 rounded-full flex-shrink-0",
                        getColorByIndex(index)
                      )}
                    />
                    <span className="font-medium truncate">{dept.name}</span>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <div
                      className="font-semibold text-gray-900"
                      title={formatCurrency(dept.budget)}
                    >
                      {formatCurrency(dept.budget)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {dept.percentage.toFixed(1)}%
                    </div>
                  </div>
                </div>
                <Progress value={dept.percentage} className="h-2" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Budget Utilization */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            Utilisation du Budget (Estimée)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {analytics.budgetUtilization
              .filter((dept) => dept.budget > 0)
              .sort((a, b) => b.utilization - a.utilization)
              .map((dept, index) => (
                <div
                  key={dept.name}
                  className="p-4 border rounded-lg space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900 truncate flex-1 min-w-0 pr-2">
                      {dept.name}
                    </h4>
                    <Badge
                      variant="secondary"
                      className={cn(
                        "flex-shrink-0",
                        dept.utilization > 80
                          ? "bg-red-100 text-red-700"
                          : dept.utilization > 60
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-green-100 text-green-700"
                      )}
                    >
                      {dept.utilization.toFixed(0)}%
                    </Badge>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Employés:</span>
                      <span className="font-medium">{dept.employees}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Budget:</span>
                      <span
                        className="font-medium"
                        title={formatCurrency(dept.budget)}
                      >
                        {formatCurrency(dept.budget)}
                      </span>
                    </div>
                  </div>

                  <Progress
                    value={dept.utilization}
                    className={cn(
                      "h-2",
                      dept.utilization > 80
                        ? "[&>div]:bg-red-500"
                        : dept.utilization > 60
                        ? "[&>div]:bg-yellow-500"
                        : "[&>div]:bg-green-500"
                    )}
                  />
                </div>
              ))}
          </div>
        </CardContent>
      </Card>

      {/* Performance Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            Insights de Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <UserCheck className="h-5 w-5 text-blue-600" />
                <h4 className="font-medium text-blue-900">
                  Départements les plus efficaces
                </h4>
              </div>
              <div className="space-y-2">
                {analytics.departmentSizes
                  .filter((d) => d.active && d.employees > 0)
                  .slice(0, 3)
                  .map((dept, index) => (
                    <div
                      key={dept.name}
                      className="flex justify-between text-sm"
                    >
                      <span className="text-blue-700">{dept.name}</span>
                      <span className="font-medium">{dept.employees} emp.</span>
                    </div>
                  ))}
              </div>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                <h4 className="font-medium text-green-900">
                  Budget le mieux utilisé
                </h4>
              </div>
              <div className="space-y-2">
                {analytics.budgetUtilization
                  .filter((d) => d.utilization > 0 && d.utilization <= 100)
                  .sort(
                    (a, b) =>
                      Math.abs(75 - a.utilization) -
                      Math.abs(75 - b.utilization)
                  )
                  .slice(0, 3)
                  .map((dept, index) => (
                    <div
                      key={dept.name}
                      className="flex justify-between text-sm"
                    >
                      <span className="text-green-700">{dept.name}</span>
                      <span className="font-medium">
                        {dept.utilization.toFixed(0)}%
                      </span>
                    </div>
                  ))}
              </div>
            </div>

            <div className="p-4 bg-orange-50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Building2 className="h-5 w-5 text-orange-600" />
                <h4 className="font-medium text-orange-900">
                  Opportunités d'amélioration
                </h4>
              </div>
              <div className="space-y-2 text-sm text-orange-700">
                {analytics.activeDepartments < analytics.totalDepartments && (
                  <div>
                    • {analytics.totalDepartments - analytics.activeDepartments}{" "}
                    département(s) inactif(s)
                  </div>
                )}
                {analytics.departmentSizes.filter(
                  (d) => !d.manager || d.manager === "Non assigné"
                ).length > 0 && (
                  <div>
                    •{" "}
                    {
                      analytics.departmentSizes.filter(
                        (d) => !d.manager || d.manager === "Non assigné"
                      ).length
                    }{" "}
                    sans manager
                  </div>
                )}
                {analytics.budgetUtilization.filter((d) => d.utilization > 90)
                  .length > 0 && (
                  <div>
                    •{" "}
                    {
                      analytics.budgetUtilization.filter(
                        (d) => d.utilization > 90
                      ).length
                    }{" "}
                    dépassement budget
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
